<script setup lang="ts">
import { useStorage } from '@vueuse/core'
import {
  SearchBotsDocument,
  SearchRepositoriesDocument,
  SearchTeamsDocument,
  SearchTeamspacesDocument,
} from './GoToSearch.generated'

export type SearchType = 'team' | 'repository' | 'teamspace' | 'bot'

interface RecentLink {
  label: string
  to: { name: string; params: Record<string, string> }
}

const props = defineProps<{
  objectType: SearchType
  showCreateButton?: boolean
}>()

const emit = defineEmits<{
  clickLink: []
  create: []
}>()

const recentTeams = useStorage<RecentLink[]>('navigation-recent-teams', [])
const recentRepositories = useStorage<RecentLink[]>('navigation-recent-repositories', [])
const recentTeamspaces = useStorage<RecentLink[]>('navigation-recent-teamspaces', [])
const recentBots = useStorage<RecentLink[]>('navigation-recent-bots', [])

const searchTerm = ref('')
const debouncedSearchTerm = refDebounced(searchTerm, 300)

const { data: teams } = useQuery(
  {
    query: SearchTeamsDocument,
    variables: computed(() => ({
      term: debouncedSearchTerm.value,
    })),
    paused: computed(() => props.objectType !== 'team'),
    tags: ['teams'],
  },
  ({ data }) => data?.organization.teams.nodes,
)

const { data: repositories } = useQuery(
  {
    query: SearchRepositoriesDocument,
    variables: computed(() => ({
      term: debouncedSearchTerm.value,
    })),
    paused: computed(() => props.objectType !== 'repository'),
  },
  ({ data }) => data?.organization.repositories.nodes,
)

const { data: bots } = useQuery(
  {
    query: SearchBotsDocument,
    paused: computed(() => props.objectType !== 'bot'),
  },
  ({ data }) => data?.bots,
)

const { data: teamspaces } = useQuery(
  {
    query: SearchTeamspacesDocument,
    variables: computed(() => ({
      term: debouncedSearchTerm.value,
    })),
    paused: computed(() => props.objectType !== 'teamspace'),
  },
  ({ data }) => data?.workspaces.nodes,
)

const teamLinks = computed(
  () =>
    teams.value?.map((team) => ({
      label: team.name,
      to: { name: 'teams-teamId' as const, params: { teamId: team.id } },
    })) ?? [],
)

const repositoryLinks = computed(() =>
  (repositories.value ?? [])
    .filter((repo) => repo.id != null)
    .map((repo) => ({
      label: `${repo.owner}/${repo.name}`,
      to: { name: 'repositories-gid' as const, params: { gid: repo.id! } },
    })),
)

const botLinks = computed(() => {
  const allBots = bots.value ?? []
  const term = debouncedSearchTerm.value.toLowerCase()
  const filtered = term ? allBots.filter((bot) => bot.name.toLowerCase().includes(term)) : allBots
  return filtered.map((bot) => ({
    label: bot.name,
    to: { name: 'bots-botSlug' as const, params: { botSlug: bot.slug } },
  }))
})

const teamspaceLinks = computed(() =>
  (teamspaces.value ?? [])
    .filter((teamspace) => !teamspace.isGlobal)
    .map((teamspace) => ({
      label: teamspace.name,
      to: { name: 'teamspace-workspaceId' as const, params: { workspaceId: teamspace.id } },
    })),
)

const links = computed(() => {
  if (props.objectType === 'team') return teamLinks.value
  if (props.objectType === 'repository') return repositoryLinks.value
  if (props.objectType === 'bot') return botLinks.value
  return teamspaceLinks.value
})

const recentLinks = computed(() => {
  if (props.objectType === 'team') return recentTeams.value
  if (props.objectType === 'repository') return recentRepositories.value
  if (props.objectType === 'bot') return recentBots.value
  return recentTeamspaces.value
})

function addToRecent(link: RecentLink) {
  const storage =
    props.objectType === 'team'
      ? recentTeams
      : props.objectType === 'repository'
        ? recentRepositories
        : props.objectType === 'bot'
          ? recentBots
          : recentTeamspaces

  const existing = storage.value.findIndex((item) => JSON.stringify(item.to) === JSON.stringify(link.to))

  if (existing !== -1) {
    storage.value.splice(existing, 1)
  }

  storage.value.unshift({ label: link.label, to: link.to })

  if (storage.value.length > 3) {
    storage.value = storage.value.slice(0, 3)
  }
}

async function handleLinkClick(link: RecentLink) {
  addToRecent(link)
  await nextTick()
  emit('clickLink')
}

const linksWithHandler = computed(() =>
  links.value.map((link) => ({
    ...link,
    click: () => handleLinkClick(link),
  })),
)

const recentLinksWithHandler = computed(() =>
  recentLinks.value.map((link) => ({
    label: link.label,
    to: link.to as any,
    click: () => handleLinkClick(link),
  })),
)

const placeholder = computed(() => {
  if (props.objectType === 'team') return 'Search teams'
  if (props.objectType === 'repository') return 'Search repositories'
  if (props.objectType === 'bot') return 'Search bots'
  return 'Search teamspaces'
})

const createLabel = computed(() => {
  if (props.objectType === 'team') return 'Create new team'
  if (props.objectType === 'bot') return 'Create new bot'
  if (props.objectType === 'teamspace') return 'Create new teamspace'
  return ''
})
</script>

<template>
  <div>
    <template v-if="recentLinks.length > 0">
      <div class="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">Recent</div>
      <UVerticalNavigation :links="recentLinksWithHandler" />
      <UDivider class="my-3" />
    </template>

    <button
      v-if="showCreateButton && createLabel"
      class="mb-3 flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-sm text-blue-600 hover:bg-gray-100 dark:text-blue-400 dark:hover:bg-gray-800"
      @click="emit('create')"
    >
      <UIcon name="light:plus" class="size-4" />
      {{ createLabel }}
    </button>

    <UInput v-model="searchTerm" icon="light:magnifying-glass" :placeholder="placeholder" autofocus />
    <UVerticalNavigation class="mt-4 max-h-60 overflow-y-auto" :links="linksWithHandler" />
  </div>
</template>
